import { computed, inject, Injectable, signal } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { DevicesDataService } from '../../../../services/data/devices-data/devices-data.service';
import {
  chartHealthSegmentsColors,
  createDeviceBadgesFn,
  DCU_DEFAULT_SEARCH_TYPE,
  DCU_SEARCH_TYPES,
  deviceTypeTableIcon
} from '../../../../shared/components/listing-page/services/listing-page-model/listing-page-model.constants';
import { getUrlPathForDeviceTypeName } from '../../../../shared/components/listing-page/services/listing-page-model/listing-page-model.helpers';
import {
  ListingDataSource,
  ListingPageModel
} from '../../../../shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import {
  batteryStatusTooltip,
  deviceHealthTooltip,
  lastHeardTooltip
} from '../../../../shared/components/listing-table/listing-page.consts';
import { ListingTableComponent } from '../../../../shared/components/listing-table/listing-table.component';
import { LocationColumnComponent } from '../../../../shared/components/table-columns/location-column.component';
import { DeviceSidePanelTitleComponent } from '../components/device-side-panel-title/device-side-panel-title.component';
import { DeviceSidePanelComponent } from '../components/device-side-panel/device-side-panel.component';
import { devicesListingSource, devicesTableSortField } from '../constants/devices.constants';
import {
  DeviceListingFieldName,
  DevicesPayload,
  DeviceType,
  OrderDirection,
  TableDevices
} from '../components/devices.models';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { StatusDotColumnComponent } from 'src/app/shared/components/listing-table/components/status-dot-column.component';
import { BubbleColumnComponent } from 'src/app/shared/components/listing-table/components/bubble-column.component';
import { TAB_VALUE } from 'src/app/shared/components/listing-page/listing-page.types';
import { ExportCsvConfig, ExportDataService } from 'src/app/services/file/export-data.service';
import { MeterTable } from 'src/app/services/data/devices-data/devices-data.types';
import { ListingPageModelAbstract } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model-abstract';
import { ListingPageContext } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-context.types';
import { DevicesMapComponent } from '@portal/shared/components/map-report-group/map-report-group.component';
import { Applicable } from 'src/app/pages/administration/report-groups/report-groups.types';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';
import { ListingPageModelRegistry } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model-registry.service';
import { DcuListingFiltersSourceService } from './filter-sources/dcu-listing-filters-source.service';
import { TimezoneService } from 'src/app/services/utility/timezone.service';

@Injectable()
export class DcuListingModelService extends ListingPageModelAbstract implements ListingPageModel {
  private readonly devices = inject(DevicesDataService);
  private readonly exportService = inject(ExportDataService);
  readonly listingPageModelRegistry = inject(ListingPageModelRegistry);
  readonly timezoneService = inject(TimezoneService);

  readonly pageTitle = TranslateRegistry.get.DCUS_BASE_STATIONS.TITLE.DCUS_BASE_STATIONS;
  readonly searchTypeList = computed(() => DCU_SEARCH_TYPES);
  readonly selectedSearchType = computed(() => DCU_DEFAULT_SEARCH_TYPE);
  readonly chartSegmentsColors = chartHealthSegmentsColors;
  readonly createBadgesFn = createDeviceBadgesFn;
  readonly sorting = computed(() => ({
    columnName: DeviceListingFieldName.LastHeard,
    orderDirection: OrderDirection.DESC
  }));
  readonly tableColumns = signal([
    {
      field: 'deviceId',
      title: TranslateRegistry.get.DCUS_BASE_STATIONS.VIEW_TABLE.COMM_DEVICE_ID,
      width: 400,
      sortable: false,
      link: {
        getLink: (dataItem: MeterTable): string =>
          `devices/${getUrlPathForDeviceTypeName(dataItem['deviceType'] as DeviceType)}/${dataItem['deviceUID']}`
      }
    },
    {
      field: 'deviceTypeName',
      title: TranslateRegistry.get.DCUS_BASE_STATIONS.VIEW_TABLE.DEVICE_TYPE,
      width: 200,
      sortable: true,
      icon: deviceTypeTableIcon
    },
    {
      field: 'modelName',
      title: TranslateRegistry.get.DCUS_BASE_STATIONS.VIEW_TABLE.MODEL_NAME,
      width: 300,
      sortable: true
    },
    {
      field: 'status',
      title: TranslateRegistry.get.DCUS_BASE_STATIONS.VIEW_TABLE.STATUS,
      width: 200,
      sortable: false,
      cmp: StatusDotColumnComponent,
      textColor: '#070C0F'
    },
    {
      field: 'dcuCommunicationStatus',
      title: TranslateRegistry.get.DCUS_BASE_STATIONS.VIEW_TABLE.COMMUNICATION_STATUS,
      width: 200,
      sortable: false
    },
    {
      field: 'deviceStatusValue',
      title: TranslateRegistry.get.DCUS_BASE_STATIONS.VIEW_TABLE.HEALTH,
      width: 200,
      sortable: true,
      cmp: BubbleColumnComponent,
      tooltip: deviceHealthTooltip
    },
    {
      field: 'lastHeard',
      title: TranslateRegistry.get.DCUS_BASE_STATIONS.VIEW_TABLE.LAST_HEARD,
      width: 200,
      sortable: true,
      tooltip: lastHeardTooltip
    },
    {
      field: 'mainBoardFw',
      title: TranslateRegistry.get.DCUS_BASE_STATIONS.VIEW_TABLE.MAIN_BOARD_FW_VERSION,
      width: 100,
      sortable: false
    },
    {
      field: 'powerSupplyBoardFw',
      title: TranslateRegistry.get.DCUS_BASE_STATIONS.VIEW_TABLE.POWER_SUPPLY_BOARD_FW_VERSION,
      width: 100,
      sortable: false
    },
    {
      field: 'slot1Fw',
      title: TranslateRegistry.get.DCUS_BASE_STATIONS.VIEW_TABLE.SLOT1_BOARD_FW_VERSION,
      width: 100,
      sortable: false
    },
    {
      field: 'slot2Fw',
      title: TranslateRegistry.get.DCUS_BASE_STATIONS.VIEW_TABLE.SLOT2_BOARD_FW_VERSION,
      width: 100,
      sortable: false
    },
    {
      field: 'slot3Fw',
      title: TranslateRegistry.get.DCUS_BASE_STATIONS.VIEW_TABLE.SLOT3_BOARD_FW_VERSION,
      width: 100,
      sortable: false
    },
    {
      field: 'batteryStatus',
      title: TranslateRegistry.get.DCUS_BASE_STATIONS.VIEW_TABLE.BATTERY_STATUS,
      width: 200,
      sortable: false,
      cmp: BubbleColumnComponent,
      tooltip: batteryStatusTooltip
    },
    {
      field: 'deviceNameCustom',
      title: TranslateRegistry.get.DCUS_BASE_STATIONS.VIEW_TABLE.DEVICE_NAME,
      width: 200,
      sortable: false
    },
    {
      field: 'deviceLocationCustom',
      title: TranslateRegistry.get.DCUS_BASE_STATIONS.VIEW_TABLE.DCU_LOCATION,
      width: 200,
      sortable: false
    },
    {
      field: 'location',
      title: TranslateRegistry.get.DCUS_BASE_STATIONS.VIEW_TABLE.LOCATION,
      width: 200,
      sortable: false,
      cmp: LocationColumnComponent
    }
  ]);
  readonly dataSource: ListingDataSource<TableDevices> = {
    getAll: tableState => {
      return this.devices.prepareDataForTable(tableState);
    },
    total: 0
  };
  readonly exportCsv = {
    dataSource: {
      export: (payload: Pick<DevicesPayload, 'filters' | 'sort'>): Observable<ExportCsvConfig[]> => {
        return this.devices.export(payload).pipe(
          map(response =>
            response.map(
              dcu =>
                ({
                  deviceId: { title: 'CommDeviceID', value: dcu.deviceId },
                  deviceTypeName: { title: 'DeviceType', value: dcu.deviceTypeName },
                  modelName: { title: 'ModelName', value: dcu.modelName },
                  status: { title: 'Status', value: dcu.status },
                  dcuCommunicationStatus: { title: 'CommunicationStatus', value: dcu.dcuCommunicationStatus },
                  deviceStatusValue: { title: 'DeviceHealth', value: dcu.deviceStatusValue },
                  lastHeard: {
                    title: 'LastHeard',
                    value: dcu.lastHeard ? this.timezoneService.formatLong(dcu.lastHeard) : ''
                  },
                  batteryStatus: { title: 'BatteryStatus', value: dcu.batteryStatus },
                  deviceNameCustom: { title: 'DeviceName', value: dcu.deviceNameCustom },
                  deviceLocationCustom: { title: 'DCULocation', value: dcu.deviceLocationCustom },
                  powerSupplyBoardFw: { title: 'PowerSupplyBoardFw', value: dcu.powerSupplyBoardFw },
                  mainBoardFW: { title: 'MainBoardFw', value: dcu.mainBoardFw },
                  slot1Fw: { title: 'Slot1Fw', value: dcu.slot1Fw },
                  slot2Fw: { title: 'Slot2Fw', value: dcu.slot2Fw },
                  slot3Fw: { title: 'Slot3Fw', value: dcu.slot3Fw },
                  location: [
                    { title: 'Latitude', value: dcu.geoMetaData?.latitude ?? '' },
                    { title: 'Longitude', value: dcu.geoMetaData?.longitude ?? '' },
                    { title: 'Comment', value: dcu.geoMetaData?.comments ?? '' }
                  ]
                }) as ExportCsvConfig
            )
          ),
          tap(data => this.exportService.exportDeviceRecordToCsv(data, 'Export-DCU', this.pageId))
        );
      }
    }
  };
  readonly selectionFieldName = 'deviceUID';
  readonly mapCmp = DevicesMapComponent;
  readonly tableCmp = ListingTableComponent;
  readonly defaultTab = TAB_VALUE.Table;
  readonly sortField = devicesTableSortField;
  readonly sidePanelBodyCmp = DeviceSidePanelComponent;
  readonly sidePanelTitleCmp = DeviceSidePanelTitleComponent;
  readonly createMapCmpInputs = (ctx: ListingPageContext): Record<string, unknown> => {
    return {
      selectedLocationData: ctx.selectedLocation(),
      filtersPayload: ctx.mapFilters(),
      chartSegmentsColors: this.chartSegmentsColors,
      createBadgesFn: this.createBadgesFn,
      locationSelectedFn: ctx.onLocationSelected,
      applicable: Applicable.AmiHealthDashboard,
      actionSource: devicesListingSource,
      pageId: this.pageId
    };
  };
  readonly pageId = ListingPageId.Dcu;
  readonly filterSource = inject(DcuListingFiltersSourceService);
  constructor() {
    super();
    const listingPageModelRegistry = this.listingPageModelRegistry;

    listingPageModelRegistry.add(this.pageId, this);
  }
}
